import { Link } from "react-router-dom";

/* ─── Types ─────────────────────────────────────────────────── */
export type ProductionJob = {
  id: string;
  title: string;
  job_type: string | null;
  workflow_status: string | null;
  status: string;
  shoot_date: string | null;
  client_name: string | null;
  updated_at: string | null;
};

export type ProductionAppointment = {
  id: string;
  title: string;
  appointment_type: string | null;
  start_at: string;
  client_name: string | null;
  location: string | null;
};

export type ProductionData = {
  allJobs: ProductionJob[];
  upcomingJobs: ProductionJob[];   // shoot_date within next 7 days
  upcomingAppts: ProductionAppointment[]; // start_at within next 48h
};

/* ─── Workflow status config ─────────────────────────────────── */
type WfConfig = { label: string; icon: string; color: string; bg: string; border: string };

const WF_CONFIG: Record<string, WfConfig> = {
  scheduled:      { label: "Scheduled",      icon: "ri-calendar-event-line",  color: "text-[#a6853e]",   bg: "bg-[#C8A96E]/10",  border: "border-[#C8A96E]/30" },
  confirmed:      { label: "Confirmed",      icon: "ri-checkbox-circle-line", color: "text-teal-700",    bg: "bg-teal-50",       border: "border-teal-200" },
  shooting:       { label: "Shooting",       icon: "ri-camera-3-line",        color: "text-amber-700",   bg: "bg-amber-50",      border: "border-amber-200" },
  editing:        { label: "Editing",        icon: "ri-edit-2-line",          color: "text-slate-700",   bg: "bg-slate-100",     border: "border-slate-300" },
  waiting_client: { label: "Waiting Client", icon: "ri-user-voice-line",      color: "text-orange-700",  bg: "bg-orange-50",     border: "border-orange-200" },
  rendering:      { label: "Rendering",      icon: "ri-loader-3-line",        color: "text-teal-700",    bg: "bg-teal-50",       border: "border-teal-200" },
  delivered:      { label: "Delivered",      icon: "ri-send-plane-fill",      color: "text-emerald-700", bg: "bg-emerald-50",    border: "border-emerald-200" },
  completed:      { label: "Completed",      icon: "ri-checkbox-circle-fill", color: "text-emerald-700", bg: "bg-emerald-50",    border: "border-emerald-200" },
  cancelled:      { label: "Cancelled",      icon: "ri-close-circle-line",    color: "text-red-600",     bg: "bg-red-50",        border: "border-red-200" },
};

const JOB_TYPE_ICONS: Record<string, string> = {
  wedding:    "ri-heart-line",
  portrait:   "ri-user-smile-line",
  event:      "ri-calendar-event-line",
  commercial: "ri-building-line",
  family:     "ri-group-line",
  newborn:    "ri-heart-2-line",
  other:      "ri-camera-line",
};

/* ─── Helpers ───────────────────────────────────────────────── */
function effectiveStatus(j: ProductionJob): string {
  return j.workflow_status || j.status || "scheduled";
}

function fmtShootDate(d: string): string {
  const date = new Date(d.includes("T") ? d : `${d}T00:00:00`);
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function daysUntil(d: string): number {
  const target = new Date(d.includes("T") ? d : `${d}T00:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function fmtApptTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function hoursUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60));
}

/* ─── Funnel bar ─────────────────────────────────────────────── */
function FunnelBar({ count, total, color }: { count: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mt-1">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/* ─── Status card ────────────────────────────────────────────── */
function StatusCard({ wfKey, count, total }: { wfKey: string; count: number; total: number }) {
  const cfg = WF_CONFIG[wfKey] ?? WF_CONFIG.scheduled;
  return (
    <Link
      to={`/admin/jobs?workflow=${wfKey}`}
      className={`flex flex-col gap-1 p-3 rounded-xl border transition-all cursor-pointer hover:opacity-80 ${cfg.bg} ${cfg.border}`}
    >
      <div className="flex items-center justify-between">
        <span className={`w-6 h-6 flex items-center justify-center rounded-lg ${cfg.bg}`}>
          <i className={`${cfg.icon} text-xs ${cfg.color}`}></i>
        </span>
        <span className={`text-xl font-bold ${cfg.color}`}>{count}</span>
      </div>
      <p className={`text-xs font-semibold ${cfg.color} leading-tight`}>{cfg.label}</p>
      <FunnelBar count={count} total={total} color={cfg.color.replace("text-", "bg-")} />
    </Link>
  );
}

/* ─── Countdown badge ────────────────────────────────────────── */
function CountdownBadge({ days }: { days: number }) {
  if (days < 0) return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 whitespace-nowrap">
      Past
    </span>
  );
  if (days === 0) return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600 animate-pulse whitespace-nowrap">
      TODAY
    </span>
  );
  if (days === 1) return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 whitespace-nowrap">
      Tomorrow
    </span>
  );
  if (days <= 3) return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
      {days}d away
    </span>
  );
  return (
    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 whitespace-nowrap">
      {days}d away
    </span>
  );
}

/* ─── Main component ─────────────────────────────────────────── */
interface Props {
  production: ProductionData;
}

export default function DashboardProduction({ production }: Props) {
  const { allJobs, upcomingJobs, upcomingAppts } = production;

  /* Status breakdown */
  const ACTIVE_STATUSES = ["scheduled", "confirmed", "shooting", "editing", "waiting_client", "rendering"];
  const DONE_STATUSES   = ["delivered", "completed"];

  const activeJobs = allJobs.filter((j) => ACTIVE_STATUSES.includes(effectiveStatus(j)));
  const totalActive = activeJobs.length;

  const countBy = (st: string) => allJobs.filter((j) => effectiveStatus(j) === st).length;

  const hasUpcoming   = upcomingJobs.length > 0;
  const hasAppts      = upcomingAppts.length > 0;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-[#C8A96E]">
            <i className="ri-camera-lens-line text-sm"></i>
          </span>
          <h2 className="text-[#0f172a] font-bold text-sm">Production Overview</h2>
        </div>
        <Link
          to="/admin/jobs"
          className="text-[#C8A96E] hover:text-[#a6853e] text-xs transition-colors cursor-pointer whitespace-nowrap"
        >
          All jobs →
        </Link>
      </div>

      {/* Status funnel — active statuses */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {ACTIVE_STATUSES.map((st) => (
          <StatusCard key={st} wfKey={st} count={countBy(st)} total={totalActive || 1} />
        ))}
      </div>

      {/* Done row */}
      <div className="grid grid-cols-2 gap-2">
        {DONE_STATUSES.map((st) => {
          const cfg = WF_CONFIG[st];
          const cnt = countBy(st);
          return (
            <Link
              key={st}
              to={`/admin/jobs?workflow=${st}`}
              className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-slate-200 hover:border-emerald-200 transition-all cursor-pointer"
            >
              <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${cfg.bg} flex-shrink-0`}>
                <i className={`${cfg.icon} text-sm ${cfg.color}`}></i>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-slate-500 text-xs">{cfg.label}</p>
                <p className={`text-lg font-bold ${cfg.color}`}>{cnt}</p>
              </div>
              <i className="ri-arrow-right-s-line text-slate-300 text-sm"></i>
            </Link>
          );
        })}
      </div>

      {/* Upcoming shoots + Appointments — side by side if both exist */}
      {(hasUpcoming || hasAppts) && (
        <div className={`grid gap-3 ${hasUpcoming && hasAppts ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>

          {/* Upcoming Shoots */}
          {hasUpcoming && (
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-amber-500">
                    <i className="ri-camera-line text-xs"></i>
                  </span>
                  <h3 className="text-[#0f172a] font-semibold text-sm">Upcoming Shoots</h3>
                  <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {upcomingJobs.length}
                  </span>
                </div>
                <span className="text-slate-400 text-[10px]">Next 7 days</span>
              </div>
              <div className="divide-y divide-slate-50">
                {upcomingJobs.map((job) => {
                  const days = job.shoot_date ? daysUntil(job.shoot_date) : null;
                  const typeIcon = JOB_TYPE_ICONS[job.job_type?.toLowerCase() ?? "other"] ?? "ri-camera-line";
                  const ws = effectiveStatus(job);
                  const cfg = WF_CONFIG[ws] ?? WF_CONFIG.scheduled;
                  return (
                    <Link
                      key={job.id}
                      to={`/admin/jobs/${job.id}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <i className={`${typeIcon} text-amber-500 text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#0f172a] text-sm font-medium truncate">{job.title}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          {job.client_name && (
                            <span className="text-slate-500 text-xs truncate">{job.client_name}</span>
                          )}
                          {job.shoot_date && (
                            <span className="text-slate-400 text-xs whitespace-nowrap">· {fmtShootDate(job.shoot_date)}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        {days !== null && <CountdownBadge days={days} />}
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${cfg.bg} ${cfg.color}`}>
                          {cfg.label}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
              {upcomingJobs.length === 0 && (
                <div className="py-8 text-center">
                  <p className="text-slate-400 text-sm">No shoots in the next 7 days</p>
                </div>
              )}
            </div>
          )}

          {/* Upcoming Appointments */}
          {hasAppts && (
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-teal-500">
                    <i className="ri-calendar-check-line text-xs"></i>
                  </span>
                  <h3 className="text-[#0f172a] font-semibold text-sm">Upcoming Appointments</h3>
                  <span className="bg-teal-100 text-teal-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {upcomingAppts.length}
                  </span>
                </div>
                <span className="text-slate-400 text-[10px]">Next 48h</span>
              </div>
              <div className="divide-y divide-slate-50">
                {upcomingAppts.map((appt) => {
                  const hrs = hoursUntil(appt.start_at);
                  return (
                    <Link
                      key={appt.id}
                      to="/admin/appointments"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-event-line text-teal-500 text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#0f172a] text-sm font-medium truncate">{appt.title}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          {appt.client_name && (
                            <span className="text-slate-500 text-xs truncate">{appt.client_name}</span>
                          )}
                          {appt.location && (
                            <span className="text-slate-400 text-xs truncate">· {appt.location}</span>
                          )}
                        </div>
                        <p className="text-slate-400 text-xs mt-0.5">{fmtApptTime(appt.start_at)}</p>
                      </div>
                      <div className="flex-shrink-0">
                        {hrs <= 0 ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600 animate-pulse whitespace-nowrap">Now</span>
                        ) : hrs <= 2 ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 whitespace-nowrap">{hrs}h</span>
                        ) : (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 whitespace-nowrap">{hrs}h away</span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state if no upcoming at all */}
      {!hasUpcoming && !hasAppts && (
        <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 text-slate-400 shrink-0">
            <i className="ri-calendar-check-line text-sm"></i>
          </span>
          <p className="text-slate-400 text-sm">No upcoming shoots or appointments in the next 7 days</p>
        </div>
      )}
    </div>
  );
}